// Libs
import { Box, List, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { MenuItem } from '~/layouts/main/components';
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum } from '~/utils/enum/common';
import { IRouteModel } from '~/utils/interface/base';
import { privateAdminRoutes } from '~/utils/routes/admin';
import { privateStaffRoutes } from '~/utils/routes/staff';
import packageJson from '../../../../../package.json';
// Styles, images, icons
import styles from './Sidebar.module.scss';
import { icons } from '~/assets';

type Props = {
  collapse: boolean;
  onToggle: () => void;
};

const cx = classNames.bind(styles);

const Sidebar = (props: Props) => {
  //#region Destructuring Props
  const { collapse, onToggle } = props;
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/sidebar', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  const routes: IRouteModel[] = useMemo(() => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        return privateAdminRoutes;

      case RoleEnum.STAFF:
        return privateStaffRoutes;

      default:
        return privateAdminRoutes;
    }
  }, [currentRole]);
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleClick = () => {
    setMenuOpen(!menuOpen);
  };

  const handleNavigateToHomePage = () => {
    // TODO: Handle logic navigate to home page all roles
  };
  //#endregion Handle Function

  return (
    <Box id='sidebarMainLayout' className={cx('container')}>
      <Box className={cx('headerSection', { collapse: collapse })}>
        <div className={cx('headerGroup')}>
          <Typography component='span'>
            {collapse ? (
              <img
                src={icons.commonIconArrowRight}
                className={cx('link')}
                alt={t('common_alt_img', { ns: 'common/base' })}
                onClick={onToggle}
              />
            ) : (
              <img
                src={icons.commonIconLogoFull}
                className={cx('link')}
                alt={t('common_alt_img', { ns: 'common/base' })}
                onClick={handleNavigateToHomePage}
              />
            )}
          </Typography>

          {!collapse && (
            <Typography component='p' className={cx('link')} onClick={onToggle}>
              <img src={icons.commonIconArrowLeft} alt={t('common_alt_img', { ns: 'common/base' })} />
            </Typography>
          )}
        </div>

        <div className={cx('line')} />
      </Box>

      <Box className={cx('menuSection')}>
        <List component='div' className={cx('menuList')}>
          {routes.map((route: IRouteModel) => {
            if (!route.path || !route?.name || !route?.activeIcon || !route?.icon) return null;

            return <MenuItem key={route.path} menuItem={route} collapse={collapse} />;
          })}
        </List>
      </Box>

      <Box className={cx('sidebarFooter')}>
        {!collapse && (
          <Box>
            <Typography component='p' className={cx('copyright')}>
              &copy;{t('copyright', { ns: 'common/sidebar' })}
            </Typography>
            <Typography component='p' className={cx('version')}>
              {packageJson.version}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Sidebar;
